/**
 * MileageEditor — bewerkbare kilometers per maand × voertuig voor één jaar.
 * - Inputs remounten per jaar (key), zodat 2025/2026 niet dezelfde stale waarden tonen.
 * - Per cel een comment-popover (zoals Airtable-comments per record).
 * Blur/save → upsert (POST als er nog geen record is, anders PATCH).
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Popover, TextField } from '@mui/material';
import { MONTH_NAMES_NL, formatInt } from '../../utils/formatters';

const T = {
  primary: '#526075', surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  tertiary: '#635983',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 16, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, ...sx }}>{name}</span>
);

export default function MileageEditor({ year, vehicles, records, onUpsert }) {
  const [cmt, setCmt] = useState(null); // { anchor, vId, month, value, mileage_km, id }

  const recMap = useMemo(() => {
    const m = {};
    (records || []).forEach((r) => { m[`${r.vehicle_material_id}-${r.month}`] = r; });
    return m;
  }, [records]);

  const commit = (vId, month, raw) => {
    const val = raw === '' ? 0 : Number(raw);
    if (Number.isNaN(val)) return;
    const existing = recMap[`${vId}-${month}`];
    if (existing && Number(existing.mileage_km) === val) return;
    if (!existing && val === 0) return;
    onUpsert({ id: existing?.id ?? null, vehicle_material_id: vId, month, year, mileage_km: val, comments: existing?.comments ?? null });
  };

  const saveComment = () => {
    if (!cmt) return;
    const existing = recMap[`${cmt.vId}-${cmt.month}`];
    onUpsert({
      id: existing?.id ?? null, vehicle_material_id: cmt.vId, month: cmt.month, year,
      mileage_km: existing ? Number(existing.mileage_km) : 0, comments: cmt.value || null,
    });
    setCmt(null);
  };

  const colTotal = (v) => MONTH_NAMES_NL.reduce((s, _, i) => s + Number(recMap[`${v.id}-${i + 1}`]?.mileage_km || 0), 0);
  const rowTotal = (month) => vehicles.reduce((s, v) => s + Number(recMap[`${v.id}-${month}`]?.mileage_km || 0), 0);

  if (!vehicles.length) {
    return <Typography sx={{ fontFamily: T.fontBody, color: T.outline, p: 2 }}>Geen actieve voertuigen.</Typography>;
  }

  const cellSx = { p: '5px 10px', borderBottom: '1px solid rgba(45,51,53,0.06)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid rgba(45,51,53,0.08)', borderRadius: '0.85rem', bgcolor: T.surface }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
        <Box component="thead">
          <Box component="tr" sx={{ bgcolor: T.surfaceLow }}>
            <Box component="th" sx={{ ...cellSx, textAlign: 'left', fontFamily: T.font, fontSize: '0.75rem', fontWeight: 700, color: T.secondary, position: 'sticky', left: 0, bgcolor: T.surfaceLow }}>Maand</Box>
            {vehicles.map((v) => (
              <Box component="th" key={v.id} sx={{ ...cellSx, fontFamily: T.font, fontSize: '0.75rem', fontWeight: 700, color: T.secondary, minWidth: 120 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: v.color || T.primary }} />{v.name}
                </Box>
              </Box>
            ))}
            <Box component="th" sx={{ ...cellSx, fontFamily: T.font, fontSize: '0.75rem', fontWeight: 800, color: T.primary }}>Totaal</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {MONTH_NAMES_NL.map((mName, i) => {
            const month = i + 1;
            return (
              <Box component="tr" key={month} sx={{ '&:hover': { bgcolor: 'rgba(82,96,117,0.03)' } }}>
                <Box component="td" sx={{ ...cellSx, textAlign: 'left', fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 600, color: T.text, position: 'sticky', left: 0, bgcolor: T.surface }}>{mName}</Box>
                {vehicles.map((v) => {
                  const rec = recMap[`${v.id}-${month}`];
                  const hasComment = !!(rec?.comments && rec.comments.trim());
                  return (
                    <Box component="td" key={v.id} sx={cellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Box
                          component="input"
                          key={`${year}-${v.id}-${month}`}
                          type="number"
                          defaultValue={rec?.mileage_km ?? ''}
                          placeholder="0"
                          onBlur={(e) => commit(v.id, month, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          sx={{
                            flex: 1, width: '100%', minWidth: 0, border: '1px solid transparent', borderRadius: '0.4rem', px: 0.8, py: 0.4,
                            textAlign: 'right', fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, bgcolor: 'transparent',
                            fontVariantNumeric: 'tabular-nums', outline: 'none',
                            '&:hover': { bgcolor: T.surfaceLow }, '&:focus': { borderColor: T.primary, bgcolor: T.surface },
                            '&::placeholder': { color: T.outlineVariant },
                          }}
                        />
                        <Box
                          onClick={(e) => setCmt({ anchor: e.currentTarget, vId: v.id, month, value: rec?.comments || '' })}
                          title={hasComment ? rec.comments : 'Notitie toevoegen'}
                          sx={{ cursor: 'pointer', color: hasComment ? T.tertiary : T.outlineVariant, display: 'inline-flex', '&:hover': { color: T.tertiary } }}
                        >
                          <Icon name={hasComment ? 'chat' : 'add_comment'} size={15} sx={{ fontVariationSettings: hasComment ? "'FILL' 1" : "'FILL' 0" }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                <Box component="td" sx={{ ...cellSx, fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: T.primary }}>{formatInt(rowTotal(month))}</Box>
              </Box>
            );
          })}
          <Box component="tr" sx={{ bgcolor: T.surfaceLow }}>
            <Box component="td" sx={{ ...cellSx, textAlign: 'left', fontFamily: T.font, fontSize: '0.8rem', fontWeight: 800, color: T.text, position: 'sticky', left: 0, bgcolor: T.surfaceLow }}>Totaal</Box>
            {vehicles.map((v) => (
              <Box component="td" key={v.id} sx={{ ...cellSx, fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 800, color: T.text }}>{formatInt(colTotal(v))}</Box>
            ))}
            <Box component="td" sx={{ ...cellSx, fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 800, color: T.primary }}>
              {formatInt(vehicles.reduce((s, v) => s + colTotal(v), 0))}
            </Box>
          </Box>
        </Box>
      </Box>

      <Popover
        open={!!cmt}
        anchorEl={cmt?.anchor}
        onClose={saveComment}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 1.5, borderRadius: '0.7rem', width: 260 } }}
      >
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', fontWeight: 700, color: T.outline, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.6 }}>
          Notitie {cmt ? `· ${MONTH_NAMES_NL[cmt.month - 1]}` : ''}
        </Typography>
        <TextField
          autoFocus multiline minRows={2} fullWidth size="small" placeholder="Bijv. winterbanden gewisseld…"
          value={cmt?.value || ''}
          onChange={(e) => setCmt((p) => ({ ...p, value: e.target.value }))}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveComment(); }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontFamily: T.fontBody, fontSize: '0.82rem' } }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.8 }}>
          <Box onClick={saveComment} sx={{ cursor: 'pointer', px: 1.6, py: 0.5, borderRadius: '0.45rem', bgcolor: T.primary, color: '#fff', fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700 }}>Opslaan</Box>
        </Box>
      </Popover>
    </Box>
  );
}
